import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider } from 'react-admin';
import { Apple, Smartphone, Save, RefreshCw, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider, AppVersion } from '../types/dataProvider';

/* ============================================================
   App version management — Latio design system (derived). Per-platform config.
   ============================================================ */

type Platform = 'ios' | 'android';
const SEMVER_REGEX = /^\d+\.\d+\.\d+$/;

const platformMeta: Record<Platform, { label: string; icon: React.ElementType; storeLabel: string; storePlaceholder: string }> = {
  ios: { label: 'iOS', icon: Apple, storeLabel: 'App Store URL', storePlaceholder: 'https://apps.apple.com/app/...' },
  android: { label: 'Android', icon: Smartphone, storeLabel: 'Play Store URL', storePlaceholder: 'https://play.google.com/store/apps/details?id=...' },
};

const PlatformVersionCard: React.FC<{ platform: Platform }> = ({ platform }) => {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const meta = platformMeta[platform];
  const Icon = meta.icon;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [minVersion, setMinVersion] = useState('');
  const [latestVersion, setLatestVersion] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [original, setOriginal] = useState<AppVersion>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await dataProvider.getAppVersion({ platform });
      setMinVersion(data.min_version || '');
      setLatestVersion(data.latest_version || '');
      setStoreUrl(data.store_url || '');
      setOriginal(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load version');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, platform]);

  useEffect(() => {
    load();
  }, [load]);

  const dirty =
    minVersion !== (original.min_version || '') ||
    latestVersion !== (original.latest_version || '') ||
    storeUrl !== (original.store_url || '');
  const minVersionValid = !minVersion || SEMVER_REGEX.test(minVersion);
  const latestVersionValid = !latestVersion || SEMVER_REGEX.test(latestVersion);
  const canSave = dirty && minVersionValid && latestVersionValid && !saving;

  const handleSave = async () => {
    const payload: Partial<AppVersion> = {};
    if (minVersion !== (original.min_version || '')) payload.min_version = minVersion;
    if (latestVersion !== (original.latest_version || '')) payload.latest_version = latestVersion;
    if (storeUrl !== (original.store_url || '')) payload.store_url = storeUrl;
    setSaving(true);
    try {
      const { data } = await dataProvider.updateAppVersion({ platform, data: payload });
      setOriginal(data);
      setMinVersion(data.min_version || '');
      setLatestVersion(data.latest_version || '');
      setStoreUrl(data.store_url || '');
      toastSuccess(`${meta.label} version updated`);
    } catch (e: any) {
      toastError(e?.message || `Failed to update ${meta.label} version`);
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    setMinVersion(original.min_version || '');
    setLatestVersion(original.latest_version || '');
    setStoreUrl(original.store_url || '');
  };

  return (
    <div className="rounded-card border border-hairline bg-background p-5">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-latio-sm bg-indigo-tint text-indigo">
            <Icon className="size-5" />
          </span>
          <div>
            <h3 className="text-[15px] font-bold text-ink">{meta.label}</h3>
            <p className="text-[12px] text-ink-3">Platform: <code>{platform}</code></p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading || saving}
          aria-label={`Refresh ${meta.label} version`}
          className="inline-flex size-8 items-center justify-center rounded-pill border border-hairline text-ink-2 hover:text-ink disabled:opacity-50"
        >
          <RefreshCw className={cn('size-4', loading && 'animate-spin')} />
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-input border border-error/30 bg-error-bg p-3 text-sm text-error">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor={`${platform}-min-version`}>Minimum required version</Label>
          <Input id={`${platform}-min-version`} value={minVersion} onChange={(e) => setMinVersion(e.target.value)} placeholder="1.0.0" disabled={loading || saving} />
          {!minVersionValid && <p className="text-xs text-error">Must be a semver string (e.g. 1.0.0)</p>}
          <p className="text-xs text-ink-3">Users on versions below this will be forced to update.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${platform}-latest-version`}>Latest available version</Label>
          <Input id={`${platform}-latest-version`} value={latestVersion} onChange={(e) => setLatestVersion(e.target.value)} placeholder="1.4.0" disabled={loading || saving} />
          {!latestVersionValid && <p className="text-xs text-error">Must be a semver string (e.g. 1.4.0)</p>}
          <p className="text-xs text-ink-3">Users below this will see an optional update prompt.</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${platform}-store-url`}>{meta.storeLabel}</Label>
          <Input id={`${platform}-store-url`} value={storeUrl} onChange={(e) => setStoreUrl(e.target.value)} placeholder={meta.storePlaceholder} disabled={loading || saving} />
        </div>
      </div>

      <div className="mt-6 flex items-center justify-between border-t border-hairline pt-4">
        <div>{dirty ? <Badge variant="secondary">Unsaved changes</Badge> : !loading && <Badge variant="outline">Up to date</Badge>}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRevert}
            disabled={!dirty || saving}
            className="inline-flex h-9 items-center rounded-pill border border-hairline px-4 text-sm font-semibold text-ink hover:bg-surface-2 disabled:opacity-50"
          >
            Revert
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-black px-4 text-sm font-semibold text-white hover:bg-ink disabled:opacity-50"
          >
            <Save className="size-4" />
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export function AppVersionsPage() {
  return (
    <div className="space-y-6 p-6" style={{ maxWidth: 1100 }}>
      <div>
        <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
          App version management
        </h1>
        <p className="mt-1 text-sm text-ink-2">
          Control minimum and latest app versions per platform. Users below the minimum are blocked until they update.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <PlatformVersionCard platform="ios" />
        <PlatformVersionCard platform="android" />
      </div>
    </div>
  );
}
